import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

const CatalogContext = createContext(null);

function stableStringify(value) {
  if (value === undefined) return "undefined";
  if (value === null) return "null";
  const valueType = typeof value;
  if (valueType === "string") return JSON.stringify(value);
  if (valueType === "number" || valueType === "boolean") {
    return String(value);
  }
  if (valueType === "function") return "[function]";
  if (Array.isArray(value)) {
    return `[${value.map((item) => stableStringify(item)).join(",")}]`;
  }
  if (valueType === "object") {
    const keys = Object.keys(value).sort();
    return `{${keys
      .map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
      .join(",")}}`;
  }
  return JSON.stringify(value);
}

function toCacheKey(key) {
  if (key === null || key === undefined || key === "") return "";
  if (typeof key === "string") return key;
  return stableStringify(key);
}

function hasKey(obj, key) {
  return Object.prototype.hasOwnProperty.call(obj, key);
}

export function CatalogProvider({ children }) {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState({});
  const [error, setError] = useState({});
  const inflightRef = useRef(new Map());
  const dataRef = useRef(data);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  const setCatalog = useCallback((key, value) => {
    const cacheKey = toCacheKey(key);
    if (!cacheKey) return;
    setData((prev) => {
      const next = { ...prev, [cacheKey]: value };
      dataRef.current = next;
      return next;
    });
    setError((prev) => ({ ...prev, [cacheKey]: undefined }));
    setLoading((prev) => ({ ...prev, [cacheKey]: false }));
  }, []);

  const invalidateCatalog = useCallback((key) => {
    const cacheKey = toCacheKey(key);
    if (!cacheKey) return;
    inflightRef.current.delete(cacheKey);
    setData((prev) => {
      if (!hasKey(prev, cacheKey)) return prev;
      const next = { ...prev };
      delete next[cacheKey];
      dataRef.current = next;
      return next;
    });
    setError((prev) => {
      if (!hasKey(prev, cacheKey)) return prev;
      const next = { ...prev };
      delete next[cacheKey];
      return next;
    });
    setLoading((prev) => {
      if (!hasKey(prev, cacheKey)) return prev;
      const next = { ...prev };
      delete next[cacheKey];
      return next;
    });
  }, []);

  const loadCatalog = useCallback((key, fetcher, options = {}) => {
    const cacheKey = toCacheKey(key);
    if (!cacheKey || typeof fetcher !== "function") {
      return Promise.resolve(undefined);
    }
    if (!options.force && hasKey(dataRef.current, cacheKey)) {
      return Promise.resolve(dataRef.current[cacheKey]);
    }
    const inflight = inflightRef.current.get(cacheKey);
    if (inflight) return inflight;

    setLoading((prev) => ({ ...prev, [cacheKey]: true }));
    const promise = Promise.resolve()
      .then(() => fetcher())
      .then((result) => {
        setData((prev) => {
          const next = { ...prev, [cacheKey]: result };
          dataRef.current = next;
          return next;
        });
        setError((prev) => ({ ...prev, [cacheKey]: undefined }));
        return result;
      })
      .catch((err) => {
        setError((prev) => ({ ...prev, [cacheKey]: err }));
        throw err;
      })
      .finally(() => {
        inflightRef.current.delete(cacheKey);
        setLoading((prev) => ({ ...prev, [cacheKey]: false }));
      });

    inflightRef.current.set(cacheKey, promise);
    return promise;
  }, []);

  const value = useMemo(
    () => ({
      data,
      loading,
      error,
      loadCatalog,
      invalidateCatalog,
      setCatalog,
    }),
    [data, loading, error, loadCatalog, invalidateCatalog, setCatalog],
  );

  return (
    <CatalogContext.Provider value={value}>
      {children}
    </CatalogContext.Provider>
  );
}

export function useCatalogContext() {
  const context = useContext(CatalogContext);
  if (!context) {
    throw new Error("useCatalog must be used within a CatalogProvider");
  }
  return context;
}

export function useCatalog(key, fetcher, options = {}) {
  const { data, loading, error, loadCatalog, invalidateCatalog } =
    useCatalogContext();
  const cacheKey = toCacheKey(key);
  const enabled = options.enabled !== false && !!cacheKey;

  useEffect(() => {
    if (!enabled) return;
    loadCatalog(cacheKey, fetcher, { force: options.force });
  }, [cacheKey, enabled, fetcher, loadCatalog, options.force]);

  const refresh = useCallback(() => {
    if (!enabled) return Promise.resolve(undefined);
    return loadCatalog(cacheKey, fetcher, { force: true });
  }, [cacheKey, enabled, fetcher, loadCatalog]);

  const invalidate = useCallback(() => {
    if (!cacheKey) return;
    invalidateCatalog(cacheKey);
  }, [cacheKey, invalidateCatalog]);

  return {
    data: cacheKey ? data[cacheKey] : undefined,
    loading: cacheKey ? !!loading[cacheKey] : false,
    error: cacheKey ? error[cacheKey] : undefined,
    refresh,
    invalidate,
  };
}
